/**
 * What a CSR is told after a resend or a contact edit SUCCEEDED (BON-472).
 *
 * CSRs ARE BLIND TO PAYMENT LINKS, and this file is where that is enforced.
 * They cannot see one, cannot open one, and have no way to check whether the
 * one a customer holds still works — so link state is only ever mentioned when
 * the CSR's own action is about to send a new one. A save that rotated nothing
 * says it saved, and stops there; the previous wording promised that "the
 * customer's existing link still works", which is unnecessary on a healthy
 * reservation and flatly untrue on one whose first email never went out.
 *
 * Three shapes, and the difference between them is the whole point:
 *
 *   1. Done — a green confirmation they can walk away from.
 *   2. SENT NOTHING, HAVING ALREADY ROTATED — the dangerous one. The write
 *      committed, so the customer's old link is dead and nothing replaced it.
 *      A toast is wrong here: it needs acknowledging.
 *   3. Nothing rotated — the save worked and no email was needed. `emailed:
 *      false` here means "nothing needed sending" and must not read as a
 *      failure.
 *
 * Failures live next door in `reservationRequestFailure.ts`, because what they
 * turn on — whether the server decided anything — has nothing to do with any of
 * this.
 */
import type {
  ResendReservationLinkResult,
  UpdateReservationContactResult,
} from '../../api/reservations';
import type { ErrorDialogTone } from '../../shared/ErrorDialog';

import { describeRequestFailure, type RotationAction } from './reservationRequestFailure';

export interface RotationAlert {
  title: string;
  message: string;
  tone: ErrorDialogTone;
}

export interface RotationOutcome {
  /** A green confirmation. Mutually exclusive with `alert`. */
  toast?: string;
  /** Something the CSR has to acknowledge. */
  alert?: RotationAlert;
  /** Whether the list must be re-read, so `stage`, `expiresAt` and
   *  `linkSendFailed` stop showing what was true before the request. */
  refresh: boolean;
}

/**
 * Where a name goes when we do not hold one. Lower case because it is only ever
 * used mid-sentence — "This replaces the link The customer already has" was the
 * capitalised version of this, on screen.
 */
export const NO_NAME = 'the customer';

/** And where an address goes when we do not hold one. The alternative was the
 *  literal word "null" interpolated into a sentence a CSR reads aloud. */
export const NO_EMAIL = 'the address on file';

/** RED, not amber. This is the more serious of the two things that can go wrong
 *  on a resend, and it used to be the softer colour of the pair. */
const SEND_FAILED_TONE: ErrorDialogTone = 'error';

/** The title says the link did NOT arrive, so it cannot be confused at a glance
 *  with the refusal dialog, whose title says nothing happened at all. */
const SEND_FAILED_TITLE = 'The payment link was not sent';

export function resendOutcome(result: ResendReservationLinkResult): RotationOutcome {
  const to = result.emailedTo || NO_EMAIL;
  if (!result.emailed) {
    return {
      alert: {
        title: SEND_FAILED_TITLE,
        message: `We couldn’t send the payment link to ${to}. Please try again.`,
        tone: SEND_FAILED_TONE,
      },
      refresh: true,
    };
  }
  return { toast: `A new payment link was sent to ${to}.`, refresh: true };
}

export function contactUpdateOutcome(result: UpdateReservationContactResult): RotationOutcome {
  if (!result.credentialRotated) {
    // Name-only, a case-only retype, or nothing changed. NO LINK LANGUAGE: the
    // CSR did not ask about the link, nothing about it moved, and telling them
    // it still works is a claim this surface cannot make.
    return { toast: 'Contact details saved.', refresh: true };
  }
  const to = result.emailedTo || NO_EMAIL;
  if (!result.emailed) {
    return {
      alert: {
        title: SEND_FAILED_TITLE,
        message:
          'The email was updated but the payment link was unable to be resent. ' +
          'Use Resend link to try sending it again.',
        tone: SEND_FAILED_TONE,
      },
      refresh: true,
    };
  }
  return { toast: `Contact details saved. A new payment link was sent to ${to}.`, refresh: true };
}

/**
 * A failed attempt, as a dialog.
 *
 * COLOUR CARRIES THE SEVERITY, and it used to carry it backwards. A refusal is
 * the benign one — the server decided before writing anything, so the customer
 * is exactly where they were — and it takes the softer tone. The two outcomes
 * that leave the customer somewhere they were not are both red: a link that
 * died without a replacement, and an attempt whose result we do not know.
 *
 * The edit form does NOT use this: its failures stay inline in the open modal so
 * a corrected address is never thrown away.
 */
export function requestFailureOutcome(action: RotationAction, err: unknown): RotationOutcome {
  const failure = describeRequestFailure(action, err);
  return {
    alert: {
      title: failure.title,
      message: failure.message,
      tone: failure.indeterminate ? 'error' : 'warning',
    },
    refresh: failure.refresh,
  };
}
